/**
 * Scheme gate for hrefs that arrive as data — CMS feeds, fetched payloads,
 * storage. ⚠ Three checks, every one against the PARSED URL and never the raw
 * string: the scheme is on the caller's allowlist, there is no userinfo, and a
 * value spelling out no scheme of its own must land on the base origin. Not a
 * host allowlist — an explicit "https://" still reaches any host.
 *
 * Ahead of those three sits the one judgement the parser cannot carry: a bare
 * "#" or "?" resolves to a perfectly valid URL and is still nowhere to send a
 * reader, so it is refused as the dead-link placeholder it is.
 */

/** The one link allowlist: every CTA, link and nav destination reads it. */
export const LINK_SCHEMES = ["https:", "http:", "tel:", "mailto:"] as const;


const BASE = "https://www.directv.com";
const BASE_ORIGIN = new URL(BASE).origin;

/* Matched against the RAW string, deliberately not the parsed one: a scheme
   the parser assembles only after deleting whitespace ("ht<tab>tps:") fails
   here, is judged relative, and the origin comparison below rejects it. */
const EXPLICIT_SCHEME = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;

/* The canonical dead-link placeholder, refused so a feed cannot dress one as a
   destination. ⚠ ONLY THE BARE FORM IS THE PLACEHOLDER — "#channels" is a real
   in-page anchor and "?q=x" a real query — so this is anchored at both ends.
   The empty alternative covers a value that is nothing but whitespace, which
   the parser resolves to the base origin and which names no destination
   either. */
const DEAD_LINK = /^[#?]?$/;

export function isSafeHref(
  href: unknown,
  schemes: readonly string[] = LINK_SCHEMES
): href is string {
  if (typeof href !== "string") {
    return false;
  }
  /* Trimmed, because the parser ignores leading and trailing whitespace before
     it resolves: " # " is the same dead link as "#". Its inner tab/LF/CR
     deletion needs no replay — that only shortens a value with characters on
     both sides of the stripped one, which can never collapse to a bare form. */
  if (DEAD_LINK.test(href.trim())) {
    return false;
  }
  let url: URL;
  try {
    /* The base anchors relative values ("#main-content", "plans"); an
       absolute value keeps its own scheme and is judged on it. */
    url = new URL(href, BASE);
  } catch {
    return false;
  }
  if (!schemes.includes(url.protocol)) {
    return false;
  }
  /* ⚠ Must stay ahead of the origin comparison, which an explicit scheme
     skips: "https://www.directv.com@evil.com/x" is an ordinary https URL
     whose host is evil.com, and this is the only check that rejects it. */
  if (url.username !== "" || url.password !== "") {
    return false;
  }
  return EXPLICIT_SCHEME.test(href) || url.origin === BASE_ORIGIN;
}
